/**
 * The owner-editable collections (contract §3.13.1, v1.2 + v1.3) in the owner's words.
 *
 * Each collection lists its form fields. The field specs carry the contract's
 * rules (required, lengths, list sizes, patterns), and formModel.js turns them
 * into form values, client-side checks and the saved `data`, so the form and
 * its checks cannot drift apart.
 *
 * Field spec:
 *   key         data key
 *   kind        'text' | 'textarea' | 'markdown' | 'date' | 'checkbox' | 'choice'
 *               | 'list' (strings) | 'entries' (objects) | 'schedule' | 'image' | 'file'
 *   label, hint plain words shown with the input
 *   required    must not be empty
 *   max         most characters
 *   empty       how an empty optional value is saved: 'omit' (key left out),
 *               'null', or '' (default)
 *   pattern / patternMessage, normalize(value) -> value, check(value, siblings) -> message
 *   list:       item (field spec), minItems, maxItems, itemLabel, addLabel
 *   entries:    fields, minItems, maxItems, itemLabel, addLabel
 *   choice:     options [{value, label, publicPath?}]; with `required` a new item
 *               starts with nothing chosen and must pick one
 *   image:      nullable
 *
 * @module content/collections
 */

/* --------------------------------------------------------------- checks */

const HTTPS_URL_RE = /^https:\/\/[^\s/?#]+\.[^\s/?#]+(?:[/?#]\S*)?$/i;
const ROOT_PATH_RE = /^\/(?!\/)\S*$/;

const hostOf = (url) => {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch (e) {
    return '';
  }
};

const isOnDomain = (url, domain) => {
  const host = hostOf(url);
  return host === domain || host.endsWith(`.${domain}`);
};

/** Banner link: a page on this site ('/faqs') or a full https address. */
const checkLinkUrl = (value) =>
  !value || ROOT_PATH_RE.test(value) || HTTPS_URL_RE.test(value)
    ? ''
    : 'Use a page on this site starting with / (like /faqs), or a full address starting with https://';

const checkLinkLabel = (value, siblings) => {
  if (!value && siblings.link_url && siblings.link_url.trim()) return 'Add the words for the link button.';
  if (value && !(siblings.link_url && siblings.link_url.trim())) return 'Add the link address too, or clear these words.';
  return '';
};

/** Group sign-up form (v1.3 `register_url`): optional, a full https address. */
export const checkRegisterUrl = (value) =>
  !value || HTTPS_URL_RE.test(value)
    ? ''
    : 'Paste the full link to your form, starting with https:// (for example https://docs.google.com/forms/...).';

const socialCheck = (domain, name) => (value) => {
  if (!value) return '';
  if (!HTTPS_URL_RE.test(value) || !isOnDomain(value, domain)) {
    return `Paste the full ${name} address, starting with https:// (for example https://www.${domain}/yourpage).`;
  }
  return '';
};

/** Tokens an FAQ answer may use (§3.13.3). Price tokens are checked by the server. */
export const FAQ_TOKENS = Object.freeze([
  'email',
  'cancel_hours',
  'late_cancel_fee',
  'payment_timing',
  'meeting_platform',
  'min_client_age',
]);

const LINK_RE = /\[[^\]\n]*\]\(([^)\s]*)\)/g;

/**
 * FAQ answer checks that mirror §3.13.3 so she hears about a problem before
 * saving: no web code, only [text](address) links to https, mailto or a page
 * on this site, and only known {{placeholders}}.
 */
export const checkFaqAnswer = (value) => {
  if (!value) return '';
  if (/<[a-zA-Z/]/.test(value)) {
    return 'Remove the "<" sign, or put a space after it. Web code can’t be used in answers.';
  }
  if (value.includes(']:')) return 'Links must be written as [words](address).';
  const withoutLinks = value.replace(LINK_RE, '');
  if (withoutLinks.includes('](')) {
    return 'A link isn’t written right. Use [words](address) with no spaces in the address.';
  }
  const targets = Array.from(value.matchAll(LINK_RE), (m) => m[1]);
  const bad = targets.find((t) => !/^https:\/\/\S+$/.test(t) && !/^mailto:\S+$/.test(t) && !ROOT_PATH_RE.test(t));
  if (bad !== undefined) {
    return `The link "${bad}" won’t work. Start it with https://, mailto: or / for a page on this site.`;
  }
  const tokens = Array.from(value.matchAll(/\{\{([^}]*)\}\}/g), (m) => m[1].trim());
  const unknown = tokens.find((t) => !FAQ_TOKENS.includes(t) && !/^price:[a-z0-9]+(-[a-z0-9]+)*$/.test(t));
  if (unknown !== undefined) return `{{${unknown}}} isn’t a placeholder the site knows. Check the list below.`;
  return '';
};

/* --------------------------------------------------------------- fields */

const TITLE = { key: 'title', kind: 'text', label: 'Title', required: true, max: 80 };

const PRICE_LINES = {
  key: 'price_lines',
  kind: 'list',
  label: 'Price lines',
  hint: 'One line each, for example "Virtual: $20 adults". You can leave these out.',
  minItems: 0,
  maxItems: 6,
  itemLabel: 'Price line',
  addLabel: 'Add a price line',
  item: { kind: 'text', required: true, max: 100 },
};

const PARAGRAPHS = {
  key: 'paragraphs',
  kind: 'list',
  label: 'Description',
  hint: 'Each box is one paragraph on the page.',
  minItems: 1,
  maxItems: 8,
  itemLabel: 'Paragraph',
  addLabel: 'Add a paragraph',
  item: { kind: 'textarea', required: true, max: 1200, rows: 5 },
};

const GROUP_FIELDS = [
  { ...TITLE, label: 'Group name' },
  { key: 'price_label', kind: 'text', label: 'Price', hint: 'For example "Free".', required: true, max: 40 },
  { key: 'audience', kind: 'text', label: 'Who it’s for', hint: 'For example "Adults 18+".', required: true, max: 60 },
  { key: 'description', kind: 'textarea', label: 'Description', required: true, max: 1200, rows: 6 },
  { key: 'schedule', kind: 'schedule', label: 'When it runs' },
  {
    key: 'register_url',
    kind: 'text',
    label: 'Sign-up form link',
    hint: 'Paste the link to your Google Form. People can still email you if “Show Email to register” is on.',
    inputMode: 'url',
    empty: 'omit',
    check: checkRegisterUrl,
  },
  {
    key: 'register_by_email',
    kind: 'checkbox',
    label: 'Show “Email to register” under this group',
    default: true,
  },
];

const WORKSHOP_FIELDS = [
  { ...TITLE, label: 'Workshop name' },
  PRICE_LINES,
  PARAGRAPHS,
  { key: 'image', kind: 'image', label: 'Picture', nullable: true },
];

const PRIVATE_GROUP_FIELDS = [
  TITLE,
  PRICE_LINES,
  PARAGRAPHS,
  {
    key: 'cta',
    kind: 'choice',
    label: 'What people are asked to do',
    options: [
      { value: 'book', label: 'Email to book' },
      { value: 'register', label: 'Email to register' },
    ],
  },
];

const stripScheme = (value) => value.replace(/^https?:\/\//i, '');
const withAt = (value) => (value && !value.startsWith('@') ? `@${value}` : value);

const RESOURCE_ENTRY_FIELDS = [
  { key: 'name', kind: 'text', label: 'Name', required: true, max: 120 },
  { key: 'location', kind: 'text', label: 'Where it is', max: 160, empty: 'omit' },
  {
    key: 'phones',
    kind: 'list',
    label: 'Phone numbers',
    minItems: 0,
    maxItems: 6,
    itemLabel: 'Phone number',
    addLabel: 'Add a phone number',
    item: {
      kind: 'text',
      inputMode: 'tel',
      required: true,
      pattern: /^[0-9+() ./-]{7,40}$/,
      patternMessage: 'Use only numbers, spaces and + ( ) . / - (at least 7).',
    },
  },
  {
    key: 'website',
    kind: 'text',
    label: 'Website',
    hint: 'Just the address, like bahamas.gov.bs (no https://).',
    inputMode: 'url',
    empty: 'omit',
    normalize: stripScheme,
    pattern: /^[a-z0-9.-]+\.[a-z]{2,}(\/\S*)?$/,
    patternMessage: 'Type the address in small letters, like example.org or example.org/help.',
  },
  {
    key: 'instagram',
    kind: 'text',
    label: 'Instagram name',
    hint: 'Like @healinghues242.',
    empty: 'omit',
    normalize: withAt,
    pattern: /^@[A-Za-z0-9._]{1,30}$/,
    patternMessage: 'Use the Instagram name, like @healinghues242 (letters, numbers, dots and _).',
  },
  { key: 'facebook', kind: 'text', label: 'Facebook page name', max: 80, empty: 'omit' },
  { key: 'description', kind: 'textarea', label: 'Notes about it', max: 600, empty: 'omit', rows: 3 },
];

const RESOURCE_FIELDS = [
  { ...TITLE, label: 'List heading', hint: 'For example "Crisis & Emergency Hotlines".' },
  {
    key: 'entries',
    kind: 'entries',
    label: 'Places in this list',
    minItems: 1,
    maxItems: 40,
    itemLabel: 'Place',
    addLabel: 'Add a place',
    fields: RESOURCE_ENTRY_FIELDS,
  },
];

const GROUNDING_FIELDS = [
  { ...TITLE, label: 'Name of the graphic' },
  { key: 'image', kind: 'image', label: 'Picture', nullable: false },
  { key: 'caption', kind: 'textarea', label: 'Caption', required: true, max: 300, rows: 3 },
];

/**
 * v1.3 "Where it shows" for gallery pictures (`album`) and downloads
 * (`placement`). Each option's publicPath is the page "View on site" opens.
 */
const ALBUM_FIELD = {
  key: 'album',
  kind: 'choice',
  label: 'Where it shows',
  required: true,
  requiredMessage: 'Choose where this picture shows.',
  options: [
    // The About page's route on this branch (App.js); update if it moves to /about.
    { value: 'about', label: 'About page', publicPath: '/about-more' },
    { value: 'groups', label: 'Groups page (photos from the free groups)', publicPath: '/groups-and-workshops' },
  ],
};

const PLACEMENT_FIELD = {
  key: 'placement',
  kind: 'choice',
  label: 'Where it shows',
  required: true,
  requiredMessage: 'Choose where this file shows.',
  options: [
    { value: 'resources', label: 'Resources page', publicPath: '/resources' },
    {
      value: 'sunday_studio',
      label: 'Under Sunday Studio on the Groups page (monthly journals)',
      publicPath: '/groups-and-workshops',
    },
  ],
};

/** v1.3: her artwork and photos (About page) and the groups' album. Alt text lives on the picture. */
const GALLERY_FIELDS = [
  ALBUM_FIELD,
  { key: 'image', kind: 'image', label: 'Picture', nullable: false },
  {
    key: 'caption',
    kind: 'textarea',
    label: 'Caption',
    hint: 'A few words shown under the picture, like “Watercolour roses”.',
    max: 300,
    rows: 3,
  },
];

const DOWNLOAD_FIELDS = [
  PLACEMENT_FIELD,
  TITLE,
  { key: 'description', kind: 'textarea', label: 'Short description', max: 600, rows: 3 },
  { key: 'file', kind: 'file', label: 'PDF file' },
];

const FAQ_FIELDS = [
  { key: 'question', kind: 'text', label: 'Question', required: true, max: 200 },
  {
    key: 'answer',
    kind: 'markdown',
    label: 'Answer',
    required: true,
    max: 3000,
    rows: 8,
    check: checkFaqAnswer,
  },
];

const BANNER_FIELDS = [
  {
    key: 'text',
    kind: 'textarea',
    label: 'Banner message',
    hint: 'One short line shown at the top of every page.',
    required: true,
    max: 160,
    rows: 3,
  },
  {
    key: 'link_url',
    kind: 'text',
    label: 'Link address',
    hint: 'A page on this site like /groups-and-workshops, or a full address starting with https://',
    inputMode: 'url',
    empty: 'null',
    check: checkLinkUrl,
  },
  { key: 'link_label', kind: 'text', label: 'Link words', hint: 'For example "See the dates".', max: 40, empty: 'null', check: checkLinkLabel },
  {
    key: 'ends_on',
    kind: 'date',
    label: 'Stop showing after',
    hint: 'The banner disappears after this day. Leave empty to keep it up.',
    empty: 'null',
  },
];

const SOCIAL_FIELDS = [
  {
    key: 'instagram_url',
    kind: 'text',
    label: 'Instagram page address',
    inputMode: 'url',
    empty: 'null',
    check: socialCheck('instagram.com', 'Instagram'),
  },
  {
    key: 'facebook_url',
    kind: 'text',
    label: 'Facebook page address',
    inputMode: 'url',
    empty: 'null',
    check: socialCheck('facebook.com', 'Facebook'),
  },
];

const note = (key, label, hint) => ({ key, kind: 'textarea', label, hint, required: true, max: 600, rows: 4 });

const NOTES_FIELDS = [
  note('group_cycle_note', 'Note under the free groups'),
  note('group_cycle_fallback', 'Line shown when a group has no upcoming dates'),
  note('workshop_note', 'Note under the workshops'),
  note('resources_disclaimer', 'Note at the top of the Resources page'),
  note('family_islands_note', 'Note for the Family Islands'),
];

/** The three fixed `settings` items, by slug. */
export const SETTINGS_ITEMS = Object.freeze({
  banner: {
    label: 'Announcement banner',
    blurb: 'A short message across the top of every page. Turn it on when you have news.',
    fields: BANNER_FIELDS,
    publicPath: '/',
  },
  social: {
    label: 'Social media links',
    blurb: 'Your Instagram and Facebook pages, linked from the site.',
    fields: SOCIAL_FIELDS,
    publicPath: '/',
  },
  notes: {
    label: 'Page notes',
    blurb: 'Short notes on the Groups and Resources pages.',
    fields: NOTES_FIELDS,
    publicPath: '/groups-and-workshops',
  },
});

/* ---------------------------------------------------------- collections */

/**
 * Collections in the order the start screen lists them. `noun` is used in
 * buttons ("Add a new group"); `publicPath` is the page "View on site" opens.
 */
export const COLLECTIONS = Object.freeze([
  {
    id: 'groups',
    label: 'Groups',
    noun: 'group',
    blurb: 'Free groups: dates, times and descriptions.',
    publicPath: '/groups-and-workshops',
    fields: GROUP_FIELDS,
  },
  {
    id: 'workshops',
    label: 'Workshops',
    noun: 'workshop',
    blurb: 'Art & Exhale and other workshops.',
    publicPath: '/groups-and-workshops',
    fields: WORKSHOP_FIELDS,
  },
  {
    id: 'private_groups',
    label: 'Private group experiences',
    noun: 'private group experience',
    blurb: 'Experiences people book for their own group.',
    publicPath: '/groups-and-workshops',
    fields: PRIVATE_GROUP_FIELDS,
  },
  {
    id: 'resources',
    label: 'Resources',
    noun: 'resource list',
    blurb: 'Helplines, clinics and other places people can turn to.',
    publicPath: '/resources',
    fields: RESOURCE_FIELDS,
  },
  {
    id: 'grounding',
    label: 'Grounding graphics',
    noun: 'grounding graphic',
    blurb: 'The calming pictures on the Resources page.',
    publicPath: '/resources',
    fields: GROUNDING_FIELDS,
  },
  {
    id: 'gallery',
    label: 'Gallery',
    noun: 'gallery picture',
    blurb: 'Your artwork and photos on the About page, and photos from the free groups on the Groups page.',
    publicPath: '/about-more',
    fields: GALLERY_FIELDS,
  },
  {
    id: 'downloads',
    label: 'Downloads',
    noun: 'download',
    blurb: 'PDFs people can save: free resources, and the monthly Sunday Studio journals.',
    publicPath: '/resources',
    fields: DOWNLOAD_FIELDS,
  },
  {
    id: 'faqs',
    label: 'FAQs',
    noun: 'question',
    blurb: 'Questions and answers.',
    publicPath: '/faqs',
    fields: FAQ_FIELDS,
  },
  {
    id: 'settings',
    label: 'Banner, social links and page notes',
    noun: 'setting',
    blurb: 'The announcement banner, your social media links and short page notes.',
    publicPath: '/',
    fixed: true,
  },
]);

export const CONTENT_HOME_PATH = '/admin/content';
export const MEDIA_LIBRARY_PATH = '/admin/content/photos';
export const collectionPath = (collection) => `${CONTENT_HOME_PATH}/${collection}`;
export const itemPath = (collection, id) => `${CONTENT_HOME_PATH}/${collection}/${id}`;

/** @returns {object|undefined} */
export const getCollection = (id) => COLLECTIONS.find((c) => c.id === id);

/** Fields of an item: per collection, or per slug for the fixed settings items. */
export const fieldsFor = (collection, slug) => {
  if (collection === 'settings') return (SETTINGS_ITEMS[slug] && SETTINGS_ITEMS[slug].fields) || [];
  const meta = getCollection(collection);
  return (meta && meta.fields) || [];
};

/**
 * Where "View on site" goes for an item: the page of its "Where it shows"
 * choice when it has one (gallery `album`, downloads `placement`), else the
 * collection's page.
 */
export const publicPathFor = (collection, slug, data) => {
  if (collection === 'settings' && SETTINGS_ITEMS[slug]) return SETTINGS_ITEMS[slug].publicPath;
  const meta = getCollection(collection);
  if (!meta) return '/';
  const chosen = (meta.fields || [])
    .filter((f) => f.kind === 'choice' && data)
    .map((f) => f.options.find((o) => o.value === data[f.key]))
    .find((o) => o && o.publicPath);
  return chosen ? chosen.publicPath : meta.publicPath;
};

const shorten = (text, max = 60) => (text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text);

/** The name an item goes by in lists. A gallery picture goes by its caption, else its description. */
export const itemTitle = (item) => {
  if (!item) return '';
  if (item.collection === 'settings' && SETTINGS_ITEMS[item.slug]) return SETTINGS_ITEMS[item.slug].label;
  const data = item.data || {};
  const named = data.title || data.question || data.text;
  if (named) return named;
  if (data.caption) return shorten(String(data.caption));
  if (data.image && data.image.alt) return shorten(String(data.image.alt));
  return data.image ? 'Picture' : 'Untitled';
};
